import { Sparkles, Send, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useSendDraft,
  useDiscardDraft,
  type InboxAiDraft,
} from "@/hooks/use-inbox-drafts";
import { useTranslation } from "react-i18next";

interface Props {
  brandId: string;
  draft: InboxAiDraft;
  /** Hands the draft text to the parent (composer / inline reply form). */
  onEdit: (text: string) => void;
}

export function DraftCard({ brandId, draft, onEdit }: Props) {
  const { t } = useTranslation("inbox");
  const sendMut = useSendDraft(brandId);
  const discardMut = useDiscardDraft(brandId);
  const busy = sendMut.isPending || discardMut.isPending;

  if (draft.status !== "PENDING") return null;

  const categoryLabel =
    t(`draftCard.category.${draft.category}`, { defaultValue: "" }) ||
    draft.category;

  return (
    <div
      data-testid="ai-draft-card"
      className="rounded-lg border border-border bg-surface-1 px-3 py-2.5"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles size={13} className="text-brand-subtle-text" />
        <span className="text-[12px] font-semibold">{t("draftCard.aiSuggestedReply")}</span>
        <span className="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-[10px] text-muted-foreground">
          {categoryLabel} · {draft.confidence}%
        </span>
      </div>
      <p className="text-[13px] whitespace-pre-wrap break-words mb-2">{draft.text}</p>
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          className="h-7 px-2.5"
          disabled={busy}
          onClick={() => sendMut.mutate({ draftId: draft.id })}
        >
          <Send size={12} />
          <span className="text-[12px]">
            {sendMut.isPending ? t("draftCard.sending") : t("draftCard.send")}
          </span>
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2.5"
          disabled={busy}
          onClick={() => onEdit(draft.text)}
        >
          <Pencil size={12} />
          <span className="text-[12px]">{t("draftCard.edit")}</span>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2.5"
          disabled={busy}
          onClick={() => discardMut.mutate(draft.id)}
        >
          <Trash2 size={12} />
          <span className="text-[12px]">{t("draftCard.discard")}</span>
        </Button>
      </div>
    </div>
  );
}
